import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { concat } from 'lodash';
import { AuthClassification } from 'src/hooks/auth/auth.interface';
import { FetchReturn, IComponentTag } from 'src/interfaces';
import { generateQuery, queryKeys, requestCCApi } from 'src/utils';

export const useGetAllComponentTags = (
  userRole?: AuthClassification | null,
  filters: string[] = [],
  enableApi: boolean = true,
) =>
  useQuery({
    queryKey: queryKeys.componentTags.all(filters),
    queryFn: () => fetchAllComponentTags(filters),
    enabled: userRole !== AuthClassification.OWNER_OPERATOR && enableApi,
  });

export const useGetAllComponentTagsWithPagination = (filters: string[] = []) =>
  useInfiniteQuery({
    queryKey: queryKeys.componentTags.pagination(filters),
    queryFn: ({ pageParam = 1 }) =>
      fetchAllComponentTags(concat(filters, [`page=${pageParam}`]), true),
    staleTime: Infinity,
    getNextPageParam: (lastPage, allPages) => {
      const currentPage = lastPage ? allPages.length : 1;
      const totalPages = lastPage ? lastPage.totalPages : 1;

      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
  });

const fetchAllComponentTags = async <T extends boolean = false>(
  filters: string[] = [],
  isPagination?: T,
): Promise<FetchReturn<T, IComponentTag[]>> => {
  const url = `/componentTags${generateQuery(
    [],
    concat(filters, isPagination ? '' : 'pageSize=0'),
  )}`;

  const response = await requestCCApi({ url });

  if (!isPagination) {
    return response.data as FetchReturn<T, IComponentTag[]>;
  } else {
    return {
      data: response.data,
      totalPages: response.totalPages ?? 0,
      totalCount: response.totalCount ?? 0,
    } as FetchReturn<T, IComponentTag[]>;
  }
};
